import { formatNumber } from '@/utils/formatValueWithUnit'
import { formatTimeline } from '@/utils/date'
import PieChartWithSummary from '@/components/molecules/PieChart/PieChart'
import { TotalEmissionsReportEntry, TotalEmissionsReportType } from '@/hooks'
import { useTranslation } from 'react-i18next'
import { formatPercentage } from '@/utils/formatPercentage'
import { EmissionData } from '@/components/molecules/PieChart/types'

type TotalEmissionsChartProps = {
    data: TotalEmissionsReportEntry[]
    startDate?: string
    endDate?: string
    total: number
    type: TotalEmissionsReportType
}

export const TotalEmissionsChart: React.FC<TotalEmissionsChartProps> = ({
    data,
    startDate,
    endDate,
    total,
    type,
}) => {
    const { t } = useTranslation('translations')

    const formatedEmissionsData = data.map(
        (
            item: TotalEmissionsReportEntry
        ): Omit<TotalEmissionsReportEntry, 'percentage'> & {
            percentage: string
        } => ({
            ...item,
            percentage: formatPercentage(item.percentage, 3),
        })
    )

    const EMISSIONS_TITLE_MAP: Record<TotalEmissionsReportType, string> = {
        emissionTypes: 'sustainability.donutChart.total_emissions',
        commodities: 'sustainability.donutChart.total_commodity_emissions',
        electricitySources:
            'sustainability.donutChart.total_electricity_mix_emissions',
    }

    const getTooltipLabel = (item: EmissionData) =>
        item.name.startsWith('Other')
            ? t('sustainability.donutChart.combined_emissions')
            : t('sustainability.donutChart.emissions')

    return (
        <PieChartWithSummary
            data={formatedEmissionsData}
            centerContent={{
                title: t(EMISSIONS_TITLE_MAP[type]),
                value: (total) => formatNumber(total),
                unit:
                    type === 'electricitySources'
                        ? t(
                              'sustainability.donutChart.total_electricity_mix_emissions_unit'
                          )
                        : t('sustainability.donutChart.unit'),
            }}
            legendContent={{
                value: (item) => item.percentage,
                title: formatTimeline({
                    startDate,
                    endDate,
                }),
            }}
            tooltipContent={(data) =>
                data.map((item) => ({
                    label: getTooltipLabel(item),
                    value: formatNumber(item.value),
                    percentage: item.percentage,
                    unit:
                        type === 'electricitySources'
                            ? t(
                                  'sustainability.donutChart.total_electricity_mix_emissions_unit'
                              )
                            : t('sustainability.donutChart.unit'),
                }))
            }
            total={total}
        />
    )
}
